// Which screens the personal Greenback Pro paywall covers, and for whom.
//
// Greenback Pro is the PERSONAL product. The paywall used to cover every
// screen under ProtectedRoute, so "Unlock Greenback" sat over Accountant HQ,
// over the client books an accountant opens from it, over the activation
// screen that promises "Your HQ is free, and stays free", and over the books
// of a business that had invited its own staff. Without Pro:
// - a practice member gets the practice and its clients' books (Dave,
//   2026-09-29: accountants use Greenback free);
// - anyone invited to a PAYING business gets the business screens (Dave,
//   2026-10-01), whatever their role;
// - field staff — a technician or dispatcher anywhere — get the business
//   side and the account screens they need (PRD 89 §4.14: "/business/*",
//   "personal screens stay paywalled" — so not the categories and rules that
//   also live under /settings), keyed on the ROLE and not on whether that
//   business pays today: a lapsed business's technicians keep read-only
//   access to their own work (PRD 42 Rule 4), and a field role can only be
//   granted while field service is set up and entitled;
// - an AR or AP clerk or read-only member of a business nobody pays for gets
//   the same screens as field staff, read-only: the api serves every member
//   the books' GETs whatever the access mode (PRD 42 Rule 4; gap G46);
// - the owner of such a business gets its billing card (Dave, 2026-10-09);
// - and everyone meets the paywall on the personal-finance screens.
//
// WHAT THIS DOES NOT OPEN. The api never consults personal Pro, so this was
// never what kept anyone out of a set of books. The api decides who may open
// which books (membership, role, firm_required) and whether they are writable
// (access mode): books nobody pays for are read-only, whoever created them.
// This only stops the PERSONAL paywall standing in front of screens that
// were never the personal product.

export type PaywallDecision = 'open' | 'paywall' | 'wait';

export const PRACTICE_SETUP_PATH = '/firm/activate';

// Manage subscription (PRD 114): where a person manages everything they pay
// Greenback for, the personal product being only one of those things. Open to
// anyone signed in, like practice setup: a firm owner without Pro, an invited
// member, and a Business owner whose group is paused or lapsed (so the Pro
// that came with it has run out) are exactly the people who need it, and the
// paywall would sit over the one screen that fixes their bill. Its query
// variants (`?account=…&task=…`) share the pathname, so they open with it.
export const MANAGE_SUBSCRIPTION_PATH = '/subscription';

// Buying Greenback Business (PRD 86 slice 5): the plan, the checkout and
// naming the first business. A business prospect is asked for Business, never
// for Pro (PRD 86 §4): Business includes Pro for its owners, and selling them
// the personal product first would charge them for it twice. So it is open to
// anyone signed in, like practice setup. Only the page itself: the rest of
// /business keeps its rules below.
export const BUSINESS_START_PATH = '/business/start';

// A business's billing card (Dave's decision 11, 2026-10-09; gap G46). Open
// to an OWNER without Pro, for /subscription's reason: an owner whose business
// lapsed, went read-only or was never paid has no bundled Pro, and the paywall
// would sit over the one screen that pays for the business. The exact path
// only (its `?business=` deep link shares it); every other business screen
// stays paywalled for them. It opens nothing writable: the api gates the books
// by access mode, and only an owner may change the subscription.
export const BUSINESS_BILLING_PATH = '/business/settings/billing';

// Where a firm's CLIENT answers their accountant: whether this firm is their
// accountant (PRD 70 §4.6, linked from the invitation email) and what it has
// asked them for (PRD 63). A 1040 client may own no business and hold no Pro,
// and paying for the personal product to reply to an accountant was never the
// deal, so these are open to anyone signed in, like practice setup.
const CLIENT_OF_A_PRACTICE_PATHS = ['/accountant-requests', '/requests'];

// Accountant HQ. Only a practice member has any use for it.
const FIRM_PREFIXES = ['/firm'];

// The books, and the account screens anybody working in them needs whether or
// not they buy Pro: settings, notifications (work reminders land there) and
// the legal pages. "/" only redirects: LandingRedirect sends a practice member
// to HQ and a member of a paying business to its books.
const BUSINESS_PREFIXES = ['/business', '/notifications', '/settings'];
const BUSINESS_EXACT = ['/', '/privacy', '/terms'];

// What field staff get without Pro (§4.14): the business side, and of the
// account screens only those a technician needs — the settings page itself,
// notification preferences, the inbox and the legal pages. Everything else
// under /settings (categories, quick categories, rules) is the personal
// product's, and stays paywalled for them.
const FIELD_STAFF_PREFIXES = ['/business', '/notifications', '/settings/notifications'];
const FIELD_STAFF_EXACT = [...BUSINESS_EXACT, '/settings'];

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export function isPracticeSetupPath(pathname: string): boolean {
  return matchesPrefix(pathname, PRACTICE_SETUP_PATH);
}

export function isManageSubscriptionPath(pathname: string): boolean {
  return matchesPrefix(pathname, MANAGE_SUBSCRIPTION_PATH);
}

export function isBusinessStartPath(pathname: string): boolean {
  return matchesPrefix(pathname, BUSINESS_START_PATH);
}

export function isBusinessBillingPath(pathname: string): boolean {
  return pathname === BUSINESS_BILLING_PATH || pathname === `${BUSINESS_BILLING_PATH}/`;
}

export function isClientOfAPracticePath(pathname: string): boolean {
  return CLIENT_OF_A_PRACTICE_PATHS.some((prefix) => matchesPrefix(pathname, prefix));
}

export function isFirmScreen(pathname: string): boolean {
  return FIRM_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));
}

/** The business side itself, where field staff also skip the waitlist (§4.14). */
export function isBusinessRoute(pathname: string): boolean {
  return matchesPrefix(pathname, '/business');
}

export function isBusinessScreen(pathname: string): boolean {
  return (
    BUSINESS_EXACT.includes(pathname) ||
    BUSINESS_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))
  );
}

/** The screens the field-staff exemption covers — a subset of isBusinessScreen. */
export function isFieldStaffScreen(pathname: string): boolean {
  return (
    FIELD_STAFF_EXACT.includes(pathname) ||
    FIELD_STAFF_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))
  );
}

// Open when any one answer is yes. Wait while one is still unknown and none
// is yes yet: deciding early would flash the paywall at a member whose answer
// is about to arrive.
function openIfAny(...answers: Array<boolean | null>): PaywallDecision {
  if (answers.some((answer) => answer === true)) return 'open';
  if (answers.some((answer) => answer === null)) return 'wait';
  return 'paywall';
}

/**
 * Whether a signed-in user sees the Pro paywall on this screen.
 *
 * `belongsToFirm`, `inPaidBusiness` and `fieldStaff` are null while still
 * being asked. On a screen they could open that is 'wait', never 'paywall':
 * drawing the paywall and then lifting it would flash "Unlock Greenback" at
 * every accountant and every invited member on their first page load.
 * `fieldStaff`, `ownsBusiness` and `readsUnpaidBusiness` left out count as no.
 */
export function paywallDecision({
  isPro,
  pathname,
  belongsToFirm,
  inPaidBusiness,
  fieldStaff = false,
  ownsBusiness = false,
  readsUnpaidBusiness = false,
}: {
  isPro: boolean;
  pathname: string;
  belongsToFirm: boolean | null;
  inPaidBusiness: boolean | null;
  fieldStaff?: boolean | null;
  ownsBusiness?: boolean | null;
  readsUnpaidBusiness?: boolean | null;
}): PaywallDecision {
  if (isPro) return 'open';
  // Anyone signed in may set up a practice. It costs nothing, and it is the
  // only way an accountant reaches the screens below.
  if (isPracticeSetupPath(pathname)) return 'open';
  if (isManageSubscriptionPath(pathname)) return 'open';
  // Before isBusinessScreen, which would ask for a paying business.
  if (isBusinessStartPath(pathname)) return 'open';
  if (isClientOfAPracticePath(pathname)) return 'open';
  if (isFirmScreen(pathname)) return openIfAny(belongsToFirm);
  if (isBusinessScreen(pathname)) {
    // Field staff count only on their own screens; elsewhere their answer is
    // no — not 'wait', so a personal screen under /settings never hangs on it.
    // Owners count only on their billing card, in the same way.
    return openIfAny(
      belongsToFirm,
      inPaidBusiness,
      isFieldStaffScreen(pathname) ? fieldStaff : false,
      isBusinessBillingPath(pathname) ? ownsBusiness : false,
      isFieldStaffScreen(pathname) ? readsUnpaidBusiness : false
    );
  }
  return 'paywall';
}
